import { ReactNode, useEffect, useState } from "react"

import { onAuthStateChanged } from "firebase/auth"

import { getComposableToken, UserNotFoundError } from "@/plugins/session/auth/composable"
import { firebaseAuth } from "@/plugins/session/auth/firebase"
import useAuthState from "@/plugins/session/auth/useAuthState"
import { LastSelectedAccountId_KEY, LastSelectedProjectId_KEY, UserSession, UserSessionContext } from "@session/UserSession"

interface UserSessionProviderProps {
    children: ReactNode | ReactNode[]
}
export function UserSessionProvider({ children }: Readonly<UserSessionProviderProps>) {
    const hashParams = new URLSearchParams(location.hash.substring(1))
    const token = hashParams.get("token")
    const state = hashParams.get("state")
    const [session, setSession] = useState<UserSession>(new UserSession())
    const { verifyState } = useAuthState()

    useEffect(() => {
        const currentUrl = new URL("https://sts.vertesia.io")
        const selectedAccount = currentUrl.searchParams.get("a") ?? localStorage.getItem(LastSelectedAccountId_KEY) ?? undefined
        const selectedProject = currentUrl.searchParams.get("p") ?? localStorage.getItem(LastSelectedProjectId_KEY + "-" + selectedAccount) ?? undefined

        if (token && state) {
            if (!verifyState(state)) {
                return
            }
            getComposableToken(selectedAccount, selectedProject, token).then(res => {
                session.login(res.rawToken).then(() => {
                    setSession(session.clone())
                    window.location.hash = ""
                })
            })
            return
        }

        return onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
            if (firebaseUser) {
                session.setSession = setSession
                await getComposableToken(selectedAccount, selectedProject).then(res => {
                    session.login(res.rawToken).then(() => setSession(session.clone()))
                }).catch(err => {
                    if (!(err instanceof UserNotFoundError)) {
                        session.logout()
                    }
                    session.isLoading = false
                    session.authError = err
                    setSession(session.clone())
                })

            } else {
                session.client.withAuthCallback()
                session.logout()
                setSession(session.clone())
            }
        })
    }, [])

    return (
        <UserSessionContext.Provider value={session}>{children}</UserSessionContext.Provider>
    )
}
